import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from "@angular/core";
import {
  MatTableDataSource,
  MatPaginator,
  PageEvent,
} from "@angular/material";
import { AppService } from "src/_services/app.service";
import { Router, ActivatedRoute } from "@angular/router";
import * as moment from "moment";

interface ProviderBet {
  betId: string;
  providerId: string;
  status: string;
  completionTime: number;
  sportId: string;
  eventId: string;
  marketId: string;
  marketName: string;
  betType: string;
  runnerId: string;
  runnerName: string;
  odd: number;
  stake: number;
  netProfit: number;
}

interface PlacedBet {
  id?: string;
  srBet: ProviderBet;
  bfBet: ProviderBet;
  createdAt: number;
  updatedAt: number;
}

@Component({
  selector: "app-placed-bets",
  templateUrl: "./placed-bets.component.html",
  styleUrls: ["./placed-bets.component.scss"],
})
export class PlacedBetsComponent implements OnInit, AfterViewInit {
  betsList: PlacedBet[] = [];
  bfBetsList = new MatTableDataSource<ProviderBet>([]);
  srBetsList = new MatTableDataSource<ProviderBet>([]);

  bfDisplayedColumns: string[] = [
    "betId",
    "completionTime",
    "marketName",
    "runnerName",
    "odd",
    "stake",
    "status",
    "netProfit",
  ];

  srDisplayedColumns: string[] = [
    "betId",
    "completionTime",
    "marketName",
    "runnerName",
    "odd",
    "stake",
    "status",
    "netProfit",
    "totalNetProfit",
  ];

  loading: boolean = false;
  eventId: string = "";
  eventName: string = "";
  marketId: string = "";
  bfMarketName: string = "";
  sportId: string = "";
  sportName: string = "";
  startTime: number = 0;
  endTime: number = 0;

  // Metrics from previous page
  totalBetsCount: number = 0;
  bfGGR: number = 0;
  srGGR: number = 0;

  // Pagination
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('bfTable') bfTable: ElementRef;
  @ViewChild('srTable') srTable: ElementRef;
  page = 1;
  pageSize = 25;
  orgPageSize = 25;
  length = 0;
  pageEvent: PageEvent;

  constructor(
    private appService: AppService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.eventId = params.eventId || "";
      this.eventName = params.eventName || "";
      this.marketId = params.marketId || "";
      this.bfMarketName = params.bfMarketName || "";
      this.sportId = params.sportId || "";
      this.sportName = params.sportName || "";
      this.startTime = params.startTime ? parseInt(params.startTime) : 0;
      this.endTime = params.endTime ? parseInt(params.endTime) : 0;
      this.totalBetsCount = params.totalBetsCount ? parseInt(params.totalBetsCount) : 0;
      this.bfGGR = params.bfGGR ? parseFloat(params.bfGGR) : 0;
      this.srGGR = params.srGGR ? parseFloat(params.srGGR) : 0;

      if (this.eventId && this.marketId && this.sportId && this.startTime && this.endTime) {
        this.getPlacedBets();
      }
    });
  }

  ngAfterViewInit() {
    // Synchronize scrolling between both tables
    setTimeout(() => {
      if (this.bfTable && this.srTable) {
        const bfTableElement = this.bfTable.nativeElement;
        const srTableElement = this.srTable.nativeElement;

        if (bfTableElement && srTableElement) {
          let isScrolling = false;

          bfTableElement.addEventListener('scroll', () => {
            if (!isScrolling) {
              isScrolling = true;
              srTableElement.scrollTop = bfTableElement.scrollTop;
              setTimeout(() => { isScrolling = false; }, 10);
            }
          });

          srTableElement.addEventListener('scroll', () => {
            if (!isScrolling) {
              isScrolling = true;
              bfTableElement.scrollTop = srTableElement.scrollTop;
              setTimeout(() => { isScrolling = false; }, 10);
            }
          });
        }
      }
    }, 100);
  }

  getPlacedBets = () => {
    if (!this.eventId || !this.marketId || !this.sportId || !this.startTime || !this.endTime) {
      this.appService.openErrorBar("Missing required parameters");
      return;
    }

    this.loading = true;
    const filters = {
      eventId: this.eventId,
      marketId: this.marketId,
      sportId: this.sportId,
      startTime: this.startTime,
      endTime: this.endTime,
      page: this.page,
      pageSize: this.orgPageSize,
    };

    this.appService.getPlacedBets(filters).subscribe(
      (res) => {
        this.loading = false;
        if (res.status === "RS_OK") {
          const data = res.bets || res.data || [];
          this.betsList = data;

          // Separate BF and SR bets
          const bfBets = data.map((bet: PlacedBet) => bet.bfBet).filter(bet => bet);
          const srBets = data.map((bet: PlacedBet) => bet.srBet).filter(bet => bet);

          this.bfBetsList = new MatTableDataSource<ProviderBet>(bfBets);
          this.srBetsList = new MatTableDataSource<ProviderBet>(srBets);

          if (this.page === 1) {
            this.length = res.totalRecords || 0;
          } else {
            if (this.paginator) {
              this.paginator.length = this.length;
            }
          }
        } else if (res.errorDescription === "Invalid Token!") {
          this.appService.openErrorBar(res.errorDescription);
          sessionStorage.clear();
          this.router.navigateByUrl("/login");
        } else {
          this.appService.openErrorBar(
            res.errorDescription || "Failed to fetch placed bets"
          );
        }
      },
      (error) => {
        this.loading = false;
        const errorMessage = (error.error && error.error.errorDescription)
          ? error.error.errorDescription
          : "Error fetching placed bets";
        this.appService.openErrorBar(errorMessage);
      }
    );
  };

  changePagination(event: PageEvent) {
    this.pageEvent = event;
    this.page = event.pageIndex + 1;
    this.orgPageSize = event.pageSize;
    this.getPlacedBets();
  }

  formatDate(date: number): string {
    if (!date) return "";
    return moment(date).format("DD-MM-YYYY HH:mm");
  }

  formatNumber(value: number): string {
    if (value === null || value === undefined) {
      return "0.00";
    }
    return value.toFixed(2);
  }

  formatGGR(value: number): string {
    if (value === null || value === undefined) {
      return "0.00";
    }
    return value.toFixed(2);
  }

  getGGRClass(value: number): string {
    if (value > 0) {
      return "ggr-positive";
    } else if (value < 0) {
      return "ggr-negative";
    }
    return "";
  }

  formatCompletionTime(time: number): string {
    if (!time) return "0";
    return time.toString() + " ms";
  }

  getBetTypeClass(betType: string): string {
    if (betType === "BACK") {
      return "bet-type-back";
    } else if (betType === "LAY") {
      return "bet-type-lay";
    }
    return "";
  }

  getRowClass(betType: string): string {
    if (betType === "BACK") {
      return "bet-row-back";
    } else if (betType === "LAY") {
      return "bet-row-lay";
    }
    return "";
  }

  getStatusClass(status: string): string {
    if (status === "OPEN") {
      return "status-open";
    } else if (status === "FAILED") {
      return "status-failed";
    } else if (status === "SETTLED") {
      return "status-settled";
    } else if (status === "VOIDED") {
      return "status-voided";
    }
    return "";
  }

  getNetProfitClass(netProfit: number): string {
    if (netProfit > 0) {
      return "profit-positive";
    } else if (netProfit < 0) {
      return "profit-negative";
    }
    return "";
  }

  getTotalNetProfit(index: number): number {
    if (index >= 0 && index < this.betsList.length) {
      const bet = this.betsList[index];
      const bfNetProfit = bet && bet.bfBet && bet.bfBet.netProfit !== undefined ? bet.bfBet.netProfit : 0;
      const srNetProfit = bet && bet.srBet && bet.srBet.netProfit !== undefined ? bet.srBet.netProfit : 0;
      return bfNetProfit + srNetProfit;
    }
    return 0;
  }

  goBack() {
    this.router.navigate(["/reports/event-arbitrage-report"], {
      queryParams: {
        sportId: this.sportId,
        sportName: this.sportName,
        startTime: this.startTime,
        endTime: this.endTime,
      },
    });
  }
}

